import { useCallback } from 'react'
import { X, Zap, ArrowRight } from 'lucide-react';
import { useSettingsModal } from '../../../contexts/SettingsModalContext';
import { useAppData } from '../../../contexts/AppDataContext';

interface OutOfCreditsModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function OutOfCreditsModal({ isOpen, onClose }: OutOfCreditsModalProps) {
  const { creditData } = useAppData();
  const { openSettings } = useSettingsModal();

  const handleAddCredits = useCallback(() => {
    onClose();
    openSettings('billing');
  }, [onClose, openSettings])

  if (!isOpen) {
    return null;
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[200]">
      <div className="rounded-2xl shadow-2xl max-w-sm w-full mx-4 relative overflow-visible bg-white">

        <button
          id="close-out-of-credits-modal"
          onClick={onClose}
          aria-label="Close modal"
          className="absolute top-4 right-4 p-1 hover:bg-white/10 rounded-full transition-colors z-10"
        >
          <X className="w-5 h-5 text-white/80" />
        </button>

        <div className="px-8 pt-10 pb-16 flex flex-col items-start text-white rounded-t-2xl bg-gradient-to-br from-neutral-900 via-neutral-800 to-neutral-900">
          <div className="flex justify-center mb-4">
            <div className="w-12 h-12 bg-white/10 rounded-xl flex items-center justify-center">
              <Zap className="w-6 h-6 text-warning-400" />
            </div>
          </div>

          <h2 className="text-2xl font-semibold text-white mb-1">
            Out of Credits
          </h2>

          <p className="text-sm text-neutral-400">
            You've used all your available credits
          </p>
        </div>

        <div className="p-6 pt-0">
          <div className="bg-white rounded-xl p-5 mb-6 flex flex-row items-center justify-between shadow-xl border border-neutral-100 -mt-10 mx-2 relative">
            <div className="flex flex-col items-start">
              <div className="flex items-baseline justify-center gap-1.5">
                <span className="text-4xl font-bold text-neutral-900">{creditData?.current_month_credits ?? 0}</span>
                <span className="text-lg text-neutral-500">credits</span>
              </div>
              <p className="text-xs text-neutral-400 mt-2">Remaining this month</p>
            </div>

            <div className="w-fit h-fit p-4 rounded-xl flex items-center justify-center bg-gradient-to-br from-neutral-900 via-neutral-800 to-neutral-900">
              <Zap className="w-5 h-5 text-warning-400" />
            </div>
          </div>

          <p className="text-neutral-600 text-sm text-center mb-6 leading-relaxed">
            Add more credits or upgrade your plan to continue generating leads and enriching data
          </p>

          <div className="space-y-3">
            <button
              id="add-credits-btn"
              onClick={handleAddCredits}
              className="w-full text-white px-6 py-3.5 rounded-xl font-semibold transition-all duration-200 shadow-lg bg-primary-600 hover:bg-primary-700 active:scale-[0.99] flex items-center justify-center gap-2"
            >
              <Zap className="w-4 h-4" />
              Add Credits
            </button>

            <button
              id="maybe-later-btn"
              onClick={onClose}
              className="w-full text-neutral-700 px-6 py-3.5 rounded-xl font-semibold transition-all duration-200 bg-neutral-100 hover:bg-neutral-200 active:scale-[0.99] flex items-center justify-center gap-2"
            >
              Maybe Later
              <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
